import { Injectable, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { KieneBackendApiService } from './kiene-backend-api.service';
import { API_BASE_URL_SERVICE } from '../../kiene-core.config';
import { HttpParams } from '@angular/common/http';
import {
    filterAktiveBetriebskontakteTierlisteVerbindungen,
    unwrapBetriebskontakteTierlisteReadResponse,
} from '../../components/gruppe-tier/tierliste-zielbetrieb-display.util';

export interface HändlerTierlisteVerbindung {
    bt_id?: number; // Primary Key der betriebskontakte_tierliste Tabelle
    id?: number; // Alias für bt_id (für Rückwärtskompatibilität)
    kontakt_id: number;
    tierliste_id: number;
    aktiv: boolean;
    bemerkung?: string;
    erstellt?: Date;
    erstellt_von?: number;
    editiert?: Date;
    editiert_von?: number;
    geloescht?: boolean;
}

export interface HändlerTierlisteVerbindungResult {
    success: boolean;
    message?: string;
    error?: string;
    id?: number;
}

@Injectable({ providedIn: 'root' })
export class BenutzerBetriebskontakteTierlisteService {
    constructor(
        private api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
    ) {}

    /**
     * Erstellt eine neue Händler-Tierliste-Verbindung
     * @param verbindung Die zu erstellende Verbindung
     * @returns Observable mit dem Ergebnis
     */
    createVerbindung(verbindung: HändlerTierlisteVerbindung): Observable<HändlerTierlisteVerbindungResult> {
        return this.api
            .post(this.apiBaseUrl + 'benutzer_betriebskontakte_tierliste/create.php', verbindung, undefined)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (this.isDuplicateTierlisteLinkError(error)) {
                        return of({ success: true });
                    }
                    return throwError(() => error);
                }),
            );
    }

    /**
     * Erstellt mehrere Händler-Tierliste-Verbindungen für eine Tierliste
     * @param kontaktIds Array der Kontakt-IDs
     * @param tierlisteId Die Tierliste-ID
     * @returns Observable mit dem Ergebnis
     */
    createVerbindungenFuerTierliste(
        kontaktIds: number[],
        tierlisteId: number,
    ): Observable<HändlerTierlisteVerbindungResult[]> {
        const verbindungen = kontaktIds.map(kontaktId => ({
            kontakt_id: kontaktId,
            tierliste_id: tierlisteId,
            aktiv: true,
            bemerkung: 'Automatisch erstellt beim Speichern der Tierliste',
        }));

        // Erstelle alle Verbindungen parallel
        const requests = verbindungen.map(verbindung => this.createVerbindung(verbindung));

        return forkJoin(requests);
    }

    /**
     * Liest alle aktiven Verbindungen für eine Tierliste
     * @param tierlisteId Die Tierliste-ID
     * @returns Observable mit den Verbindungen
     */
    getVerbindungenFuerTierliste(tierlisteId: number): Observable<HändlerTierlisteVerbindung[]> {
        const params = new HttpParams().set('tierliste_id', tierlisteId.toString());
        return this.api.get(this.apiBaseUrl + 'benutzer_betriebskontakte_tierliste/read.php', params);
    }

    /**
     * Liest alle aktiven Verbindungen für einen Kontakt
     * @param kontaktId Die Kontakt-ID
     * @returns Observable mit den Verbindungen
     */
    getVerbindungenFuerKontakt(kontaktId: number): Observable<HändlerTierlisteVerbindung[]> {
        const params = new HttpParams().set('kontakt_id', kontaktId.toString());
        return this.api.get(this.apiBaseUrl + 'benutzer_betriebskontakte_tierliste/read.php', params);
    }

    /**
     * Aktualisiert die Kontakte für eine Tierliste
     * Vergleicht bestehende Einträge mit neuen und führt entsprechende Änderungen durch
     * @param neueKontaktIds Array der neuen Kontakt-IDs
     * @param tierlisteId Die Tierliste-ID
     * @returns Observable mit dem Ergebnis
     */
    updateKontakteFuerTierliste(
        neueKontaktIds: number[],
        tierlisteId: number,
        options?: { allowClearAll?: boolean; kontaktKundeIds?: Record<number, number> },
    ): Observable<HändlerTierlisteVerbindungResult[]> {
        return new Observable(observer => {
            const normalizedNeueKontaktIds = [
                ...new Set(neueKontaktIds.map(id => Number(id)).filter(id => id > 0)),
            ];
            if (normalizedNeueKontaktIds.length === 0 && !options?.allowClearAll) {
                observer.next([]);
                observer.complete();
                return;
            }

            this.getVerbindungenFuerTierliste(tierlisteId).subscribe({
                next: bestehendeVerbindungen => {
                    const verbindungen = filterAktiveBetriebskontakteTierlisteVerbindungen(
                        unwrapBetriebskontakteTierlisteReadResponse(bestehendeVerbindungen),
                    );
                    const bestehendeKontaktIds = new Set(
                        verbindungen.map((v: any) => Number(v.kontakt_id)).filter(id => id > 0),
                    );
                    const bestehendeKundenIds = new Set(
                        verbindungen
                            .map((v: any) => Number(v.kunden_id ?? v.kunde_id))
                            .filter(id => id > 0),
                    );
                    const neueKontaktIdSet = new Set(normalizedNeueKontaktIds);

                    const operations: Observable<HändlerTierlisteVerbindungResult>[] = [];

                    const queueSoftDelete = (verbindung: any): void => {
                        const btId = Number(verbindung?.bt_id ?? verbindung?.id);
                        if (btId > 0) {
                            operations.push(this.softDeleteVerbindung(btId));
                        }
                    };

                    if (normalizedNeueKontaktIds.length === 0 && options?.allowClearAll) {
                        for (const verbindung of verbindungen) {
                            queueSoftDelete(verbindung);
                        }
                    } else {
                        const zuLoeschendeIds = [...bestehendeKontaktIds].filter(id => !neueKontaktIdSet.has(id));
                        zuLoeschendeIds.forEach(kontaktId => {
                            verbindungen
                                .filter((v: any) => Number(v.kontakt_id) === kontaktId)
                                .forEach(queueSoftDelete);
                        });

                        const neueVerbindungen = normalizedNeueKontaktIds.filter(id => {
                            if (bestehendeKontaktIds.has(id)) {
                                return false;
                            }
                            const kundenId = Number(options?.kontaktKundeIds?.[id] ?? 0);
                            return !(kundenId > 0 && bestehendeKundenIds.has(kundenId));
                        });

                        neueVerbindungen.forEach(kontaktId => {
                            const neueVerbindung: HändlerTierlisteVerbindung = {
                                kontakt_id: kontaktId,
                                tierliste_id: tierlisteId,
                                aktiv: true,
                                bemerkung: 'Automatisch erstellt beim Speichern der Tierliste',
                            };
                            operations.push(this.createVerbindung(neueVerbindung));
                        });
                    }

                    if (operations.length === 0) {
                        observer.next([]);
                        observer.complete();
                        return;
                    }

                    // 5. Alle Operationen ausführen
                    forkJoin(operations).subscribe({
                        next: results => {
                            observer.next(results);
                            observer.complete();
                        },
                        error: error => {
                            observer.error(error);
                        },
                    });
                },
                error: error => {
                    observer.error(error);
                },
            });
        });
    }

    /**
     * Löscht eine Verbindung (Hard Delete)
     * @param btId Die bt_id der Verbindung (Primary Key der betriebskontakte_tierliste Tabelle)
     * @returns Observable mit dem Ergebnis
     */
    deleteVerbindung(btId: number): Observable<HändlerTierlisteVerbindungResult> {
        return this.api.post(
            this.apiBaseUrl + 'benutzer_betriebskontakte_tierliste/delete.php',
            { id: btId },
            undefined,
        );
    }

    /**
     * Setzt eine Verbindung auf geloescht = benutzerid (Soft Delete)
     * @param btId Die bt_id der Verbindung (Primary Key der betriebskontakte_tierliste Tabelle)
     * @returns Observable mit dem Ergebnis
     */
    softDeleteVerbindung(btId: number): Observable<HändlerTierlisteVerbindungResult> {
        return this.api.post(
            this.apiBaseUrl + 'benutzer_betriebskontakte_tierliste/delete.php',
            { id: btId },
            undefined,
        );
    }

    /**
     * Admin-Methode: Lädt alle Kontakte für einen bestimmten Betrieb von allen Benutzern
     * @param betriebId Die Betriebs-ID
     * @returns Observable mit allen Kontakten des Betriebs
     */
    getAlleKontakteFuerBetrieb(betriebId: number): Observable<any[]> {
        const params = new HttpParams().set('betrieb_id', betriebId.toString());
        return this.api.get(this.apiBaseUrl + 'benutzer_betriebskontakte/read_by_betrieb_id.php', params);
    }

    private isDuplicateTierlisteLinkError(error: HttpErrorResponse): boolean {
        const fehler = String(error?.error?.fehler ?? error?.error?.errortext ?? '');
        return fehler.includes('Verknüpfung existiert bereits') || fehler.includes('Verknuepfung existiert bereits');
    }
}
